import React, { useEffect, useRef, useState } from 'react';
import { Input, Button, Row, Col, Radio, Checkbox, message, Modal, Select } from 'antd';
import { SearchOutlined, PlusOutlined, RightOutlined, SaveOutlined, TagOutlined, DownloadOutlined } from '@ant-design/icons';
import './knowYourRole.css';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Add_Activity, Get_Activity_By_Role, Add_Task, Get_Task_By_Activity, Add_Subtask_By_Task, Get_Subtask_By_Task, currentUserProfileQuery, Get_LeadIndicatorFunnel, SAVE_SUBTASK_TAGGING, Export_Activities_Tasks_Subtask_Excel } from "../../../../services/userAPI";
import { useLocation } from 'react-router-dom';
import EditIcon from "../../../../Images/knowYourRoleEditIcon.png";
import DeleteIcon from "../../../../Images/knowYourRoleDeleteIcon.png";
import DiamondIcon from "../../../../Images/diamondIcon.png";
import dayjs from 'dayjs';
import AddingPlus from "../../../../Images/addingPluscon.png";
import SubtaskTaggingModal from './SubtaskTaggingModal';
import PageBreadcrumb from '../../../../reusableField/PageBreadcrumb';

const KnowYourRole = () => {
  // State for search inputs
  const [activitySearch, setActivitySearch] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [subTaskSearch, setSubTaskSearch] = useState('');

  // Form visibility states
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showSubTaskForm, setShowSubTaskForm] = useState(false);

  // Form input states
  const [activityTitle, setActivityTitle] = useState('');
  const [taskTitle, setTaskTitle] = useState('');
  const [subTaskTitle, setSubTaskTitle] = useState('');

  // Selected items
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedSubTask, setSelectedSubTask] = useState(null);

  // Details form state
  const [description, setDescription] = useState('');
  const [isCritical, setIsCritical] = useState(false);
  const [dateCreated, setDateCreated] = useState('');

  const [isCriticalSubtask, setIsCriticalSubtask] = useState(false);
  const [nonCriticalDesc, setNonCriticalDesc] = useState('');

  const [isEditingActivity, setIsEditingActivity] = useState(false);
  const [editingActivityId, setEditingActivityId] = useState(null);

  const [isEditingTask, setIsEditingTask] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);

  const [isEditingSubTask, setIsEditingSubTask] = useState(false);
  const [editingSubTaskId, setEditingSubTaskId] = useState(null);

  const [isEditingDetails, setIsEditingDetails] = useState(false);

  // State for radio buttons when adding new items
  const [activityPosition, setActivityPosition] = useState(null); // "before" or "after"
  const [taskPosition, setTaskPosition] = useState(null); // "before" or "after"
  const [subTaskPosition, setSubTaskPosition] = useState(null); // "before" or "after"

  const [isSavingActivity, setIsSavingActivity] = useState(false);
  const [isSavingTask, setIsSavingTask] = useState(false);
  const [isSavingSubTask, setIsSavingSubTask] = useState(false);
  const [isSavingDetails, setIsSavingDetails] = useState(false);

  // Lead Indicator multi-select state
  const [selectedLeadIndicators, setSelectedLeadIndicators] = useState([]);

  // Subtask Tagging Modal state
  const [showTaggingModal, setShowTaggingModal] = useState(false);

  const [deleteModal, setDeleteModal] = useState({
    visible: false,
    type: '',        // 'activity' | 'task' | 'subtask'
    id: null,
    name: '',
    onConfirm: null,
    isDeleting: false,
  });

  const showDeleteConfirm = (type, id, name, onConfirm) => {
    setDeleteModal({
      visible: true,
      type,
      id,
      name,
      onConfirm,
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteModal.onConfirm) {
      setDeleteModal({ ...deleteModal, isDeleting: true });
      try {
        await deleteModal.onConfirm();
      } finally {
        setDeleteModal({ ...deleteModal, visible: false, isDeleting: false });
      }
    } else {
      setDeleteModal({ ...deleteModal, visible: false });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ ...deleteModal, visible: false });
  };

  const inputRefActivity = useRef(null);
  const inputRefTask = useRef(null);
  const inputRefSubTask = useRef(null);

  useEffect(() => {
    if (inputRefActivity.current) {
      inputRefActivity.current.focus();
    }
  }, []);

  const location = useLocation();
  const roleId = location.state?.roleId;
  const accountId = location.state?.accountId;
  const accountName = location.state?.accountName;
  const safeRoleId = roleId;
  const safeAccountId = accountId;
  const safeAccountName = accountName;

  // Fetch current user profile to check role
  const { data: userProfileData } = useQuery(currentUserProfileQuery, {
    fetchPolicy: 'cache-first',
  });
  const isRootAdmin = userProfileData?.currentUserProfile?.role?.toLowerCase() === 'root admin';

  // Handle add activity
  const handleAddActivity = (afterActivityId = null) => {
    setShowActivityForm(true);
    setActivityAfterIdForAdd(afterActivityId !== null ? afterActivityId : 0);
    setActivityPosition(null);
  };

  const [activityAfterIdForAdd, setActivityAfterIdForAdd] = useState(0);

  // Handle add task
  const handleAddTask = (afterTaskId = null) => {
    setShowTaskForm(true);
    setTaskAfterIdForAdd(afterTaskId !== null ? afterTaskId : 0);
    setTaskPosition(null);
  };

  const [taskAfterIdForAdd, setTaskAfterIdForAdd] = useState(0);

  // Handle add sub-task
  const handleAddSubTask = (afterSubTaskId = null) => {
    setShowSubTaskForm(true);
    setSubTaskAfterIdForAdd(afterSubTaskId !== null ? afterSubTaskId : 0);
    setSubTaskPosition(null);
  };

  const [subTaskAfterIdForAdd, setSubTaskAfterIdForAdd] = useState(0);

  const [addActivityMutation] = useMutation(Add_Activity);

  const [fetchActivities, { data: activitiesData, refetch }] = useLazyQuery(Get_Activity_By_Role, {
    variables: {
      jobroleId: roleId,
      fullHierarchy: false,
      pagingCriteria: null,
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (roleId) {
      fetchActivities();
    }
  }, [roleId]);

  useEffect(() => {
    if (!roleId) return;
    fetchActivities({
      variables: {
        jobroleId: roleId,
        fullHierarchy: false,
        pagingCriteria: null,
        searchTerm: activitySearch.trim() || null,
      }
    });
  }, [roleId, activitySearch, fetchActivities]);

  const activities = [...(activitiesData?.listActivities?.data || [])];

  const [addTaskMutation] = useMutation(Add_Task);
  const [addSubtaskMutation] = useMutation(Add_Subtask_By_Task);
  const [saveSubtaskTaggingMutation] = useMutation(SAVE_SUBTASK_TAGGING);

  const [exportExcel, { loading: exportLoading }] = useLazyQuery(Export_Activities_Tasks_Subtask_Excel, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const url = data?.exportActivitiesTasksSubtaskExcel;
      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', '');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success('Export downloaded successfully');
      } else {
        message.error('No export data received');
      }
    },
    onError: (error) => {
      console.error('Export error:', error);
      message.error('Failed to export data');
    },
  });

  const handleExportCSV = () => {
    if (!roleId) {
      message.warning('Role ID is not available');
      return;
    }
    exportExcel({ variables: { jobroleId: parseInt(roleId) } });
  };

  // Fetch lead indicators for the role
  const { data: leadIndicatorData } = useQuery(Get_LeadIndicatorFunnel, {
    variables: { roleId: roleId ? parseInt(roleId) : 0 },
    skip: !roleId,
    fetchPolicy: 'network-only',
  });

  const leadIndicatorOptions = (leadIndicatorData?.getKiisForRole || []).map((li) => ({
    label: li.name,
    value: li.id,
  }));

  const [fetchTasks, { data: tasksData, refetch: refetchTasks }] = useLazyQuery(Get_Task_By_Activity, {
    fetchPolicy: 'network-only'
  });

  // Auto-select first activity (index 0) when activities data loads
  useEffect(() => {
    if (activities.length > 0 && !selectedActivity) {
      const firstActivity = activities[0];
      setSelectedActivity(firstActivity);
      fetchTasks({ variables: { activityId: firstActivity.id, roleId: roleId } });
    }
  }, [activitiesData, fetchTasks]);

  const { data: subtaskData, loading, error, refetch: refetchSubtasks } = useQuery(Get_Subtask_By_Task, {
    variables: {
      taskId: selectedTask?.id || 0,
      fullHierarchy: false,
      searchTerm: ""
    },
    skip: !selectedTask,
  });

  useEffect(() => {
    if (!selectedActivity) return;
    fetchTasks({
      variables: {
        activityId: selectedActivity.id,
        roleId: roleId,
        searchTerm: taskSearch.trim() || null,
      }
    });
  }, [selectedActivity, taskSearch, fetchTasks, roleId]);

  useEffect(() => {
    if (selectedTask) {
      refetchSubtasks({
        taskId: selectedTask.id,
        fullHierarchy: false,
        searchTerm: subTaskSearch.trim() || null,
      });
    }
  }, [selectedTask, subTaskSearch, refetchSubtasks]);

  useEffect(() => {
    const tasks = tasksData?.getTaskByActivity || [];
    if (tasks.length > 0 && !selectedTask) {
      setSelectedTask(tasks[0]);
    }
  }, [tasksData]);

  useEffect(() => {
    const subtasks = subtaskData?.listSubtasksForTask?.filter(st => st.taskId === selectedTask?.id) || [];
    if (subtasks.length > 0 && !selectedSubTask) {
      const firstSubTask = subtasks[0];
      setSelectedSubTask(firstSubTask);
      setIsCritical(firstSubTask.isCritical);
      setDescription(firstSubTask.nonCriticalDescription || '');
      const formattedDate = firstSubTask.createdAt
        ? dayjs(firstSubTask.createdAt).format('DD/MM/YYYY')
        : '';
      setDateCreated(formattedDate);
    }
  }, [subtaskData, selectedTask]);

  const handleSaveActivity = async () => {
    if (isSavingActivity) return;

    if (activityTitle.trim()) {
      setIsSavingActivity(true);
      try {
        let finalAfterActivityId = -1; // Default to -1 for new activities
        const activitiesList = activities || [];

        // Only apply position logic if activityAfterIdForAdd is not 0 and position is specified
        if (activityPosition && activityAfterIdForAdd !== 0) {
          const currentIndex = activitiesList.findIndex(activity => activity.id === activityAfterIdForAdd);
          if (currentIndex !== -1) {
            if (activityPosition === 'before') {
              finalAfterActivityId = currentIndex > 0 ? activitiesList[currentIndex - 1].id : 0;
            } else {
              finalAfterActivityId = activityAfterIdForAdd;
            }
          }
        }

        const variables = {
          jobroleId: roleId,
          name: activityTitle,
          afterActivityId: finalAfterActivityId,
        };

        if (isEditingActivity && editingActivityId) {
          variables.activityId = editingActivityId;
        }

        const { data } = await addActivityMutation({
          variables: variables,
        });

        if (data?.addActivity?.success) {
          message.success(data.addActivity.message || 'Activity saved successfully');
          setActivityTitle('');
          setShowActivityForm(false);
          setIsEditingActivity(false);
          setEditingActivityId(null);
          setActivityAfterIdForAdd(0);
          setActivityPosition(null);

          await refetch();

          const updatedActivities = await fetchActivities();
          const newActivity = updatedActivities?.data?.listActivities?.data?.find(
            (activity) => activity.name === activityTitle || activity.id === editingActivityId
          );

          if (newActivity) {
            setSelectedActivity(newActivity);
            setSelectedTask(null);
            setSelectedSubTask(null);
            fetchTasks({ variables: { activityId: newActivity.id, roleId: roleId } });
          }

          refetch();
        } else {
          message.error('Activity operation failed: ' + (data?.addActivity?.message || 'Unknown error'));
        }
      } catch (err) {
        console.error('Error with activity operation:', err);
        message.error('Something went wrong while saving the activity');
      } finally {
        setIsSavingActivity(false);
      }
    }
  };

  const handleSaveTask = async () => {
    if (isSavingTask) return;

    if (taskTitle.trim() && selectedActivity) {
      setIsSavingTask(true);
      try {
        let finalAfterTaskId = -1; // Default to -1 for new tasks
        const tasks = tasksData?.getTaskByActivity || [];

        // Only apply position logic if taskAfterIdForAdd is not 0 and position is specified
        if (!isEditingTask && taskPosition && taskAfterIdForAdd !== 0) {
          const currentIndex = tasks.findIndex(task => task.id === taskAfterIdForAdd);
          if (currentIndex !== -1) {
            if (taskPosition === 'before') {
              finalAfterTaskId = currentIndex > 0 ? tasks[currentIndex - 1].id : 0;
            } else {
              finalAfterTaskId = taskAfterIdForAdd;
            }
          }
        }

        const variables = {
          activityId: selectedActivity.id,
          name: taskTitle,
          afterTaskId: finalAfterTaskId,
        };

        if (isEditingTask && editingTaskId) {
          variables.taskId = editingTaskId;
        }

        const { data } = await addTaskMutation({
          variables: variables,
        });

        if (data?.addTasks?.success) {
          message.success(data.addTasks.message || 'Task saved successfully');

          setTaskTitle('');
          setShowTaskForm(false);
          setIsEditingTask(false);
          setEditingTaskId(null);
          setTaskAfterIdForAdd(0);
          setTaskPosition(null);

          const updatedTasks = await fetchTasks({
            variables: {
              activityId: selectedActivity.id,
              roleId: roleId,
              searchTerm: taskSearch.trim() || null,
            },
          });

          const newTask = updatedTasks?.data?.getTaskByActivity?.find(
            (task) => task.name === taskTitle || task.id === editingTaskId
          );

          if (newTask) {
            setSelectedTask(newTask);
            refetchSubtasks({
              variables: {
                taskId: newTask.id,
                fullHierarchy: false,
                searchTerm: subTaskSearch.trim() || null,
              },
            });
          }

          setTaskTitle('');
          setShowTaskForm(false);
          setIsEditingTask(false);
          setEditingTaskId(null);
          setTaskAfterIdForAdd(0);
          setTaskPosition(null);
        } else {
          message.error(data?.addTasks?.message || 'Failed to save task');
        }
      } catch (err) {
        console.error('Error with task operation:', err);
        message.error('Something went wrong while saving the task');
      } finally {
        setIsSavingTask(false);
      }
    }
  };

  const handleSaveSubTask = async () => {
    if (isSavingSubTask) return;

    try {
      setIsSavingSubTask(true);
      let finalAfterSubTaskId = -1; // Default to -1 for new subtasks
      const subtasks = subtaskData?.listSubtasksForTask || [];

      // Only apply position logic if subTaskAfterIdForAdd is not 0 and position is specified
      if (!isEditingSubTask && subTaskPosition && subTaskAfterIdForAdd !== 0) {
        const currentIndex = subtasks.findIndex(subtask => subtask.id === subTaskAfterIdForAdd);
        if (currentIndex !== -1) {
          if (subTaskPosition === 'before') {
            finalAfterSubTaskId = currentIndex > 0 ? subtasks[currentIndex - 1].id : 0;
          } else {
            finalAfterSubTaskId = subTaskAfterIdForAdd;
          }
        }
      }

      const variables = {
        taskId: selectedTask.id,
        name: subTaskTitle,
        isCritical: isCriticalSubtask,
        nonCriticalDescription: isCriticalSubtask ? '' : nonCriticalDesc,
        rankForRecommendation: 0,
        afterSubTaskId: finalAfterSubTaskId,
        relatedKii: selectedLeadIndicators,
      };

      if (isEditingSubTask && editingSubTaskId) {
        variables.subtaskId = editingSubTaskId;
      }

      const { data } = await addSubtaskMutation({
        variables: variables,
      });

      if (data?.addSubtasks?.success) {
        // Save lead indicator tagging if any lead indicators were selected
        const savedSubtaskId = isEditingSubTask ? editingSubTaskId : data.addSubtasks.data?.id;
        if (savedSubtaskId && selectedLeadIndicators.length > 0) {
          try {
            await saveSubtaskTaggingMutation({
              variables: {
                subtaskId: savedSubtaskId,
                leadIndicatorIds: selectedLeadIndicators,
                videoIds: [],
              },
            });
          } catch (tagErr) {
            console.error('Error saving lead indicator tagging:', tagErr);
          }
        }

        message.success(data.addSubtasks.message || 'Subtask saved successfully');

        setSubTaskTitle('');
        setIsCriticalSubtask(false);
        setNonCriticalDesc('');
        setSelectedLeadIndicators([]);
        setShowSubTaskForm(false);
        setIsEditingSubTask(false);
        setEditingSubTaskId(null);
        setSubTaskAfterIdForAdd(0);
        setSubTaskPosition(null);

        const updatedSubtasks = await refetchSubtasks({
          taskId: selectedTask.id,
          fullHierarchy: false,
          searchTerm: subTaskSearch.trim() || null,
        });

        const newSubTask = updatedSubtasks?.data?.listSubtasksForTask?.find(
          (subTask) => subTask.name === subTaskTitle || subTask.id === editingSubTaskId
        );

        if (newSubTask) {
          setSelectedSubTask(newSubTask);
          setIsCritical(newSubTask.isCritical);
          setDescription(newSubTask.nonCriticalDescription || '');
          const formattedDate = newSubTask.createdAt
            ? dayjs(newSubTask.createdAt).format('DD/MM/YYYY')
            : '';
          setDateCreated(formattedDate);
        }

        setSubTaskTitle('');
        setIsCriticalSubtask(false);
        setNonCriticalDesc('');
        setSelectedLeadIndicators([]);
        setShowSubTaskForm(false);
        setIsEditingSubTask(false);
        setEditingSubTaskId(null);
        setSubTaskAfterIdForAdd(0);
        setSubTaskPosition(null);
      } else {
        message.error(data?.addSubtasks?.message || 'Failed to save subtask');
      }
    } catch (err) {
      console.error('Error with subtask operation:', err);
      message.error('Something went wrong while saving the subtask');
    } finally {
      setIsSavingSubTask(false);
    }
  };

  const handleSaveDetails = async () => {
    if (isSavingDetails) return;
    if (selectedSubTask && description.trim()) {
      try {
        setIsSavingDetails(true);
        const { data } = await addSubtaskMutation({
          variables: {
            taskId: selectedSubTask.taskId,
            name: selectedSubTask.name,
            isCritical: isCritical,
            nonCriticalDescription: description,
            rankForRecommendation: 0,
            afterSubTaskId: -1,
            subtaskId: selectedSubTask.id,
            relatedKii: (selectedSubTask.kiis || []).map(k => k.id),
          },
        });

        if (data?.addSubtasks?.success) {
          message.success(data.addSubtasks.message || 'Subtask details updated successfully');
          await refetchSubtasks();
          setSelectedSubTask({
            ...selectedSubTask,
            isCritical: isCritical,
            nonCriticalDescription: description
          });
          setIsEditingDetails(false);
        } else {
          message.error(data?.addSubtasks?.message);
        }
      } catch (err) {
        console.error('Error updating subtask details:', err);
      } finally {
        setIsSavingDetails(false);
      }
    }
  };

  const handleDeleteActivity = async (activityId) => {
    try {
      const { data } = await addActivityMutation({
        variables: {
          jobroleId: roleId,
          name: '',
          afterActivityId: -1,
          activityId: activityId,
          status: 0
        }
      });

      if (data?.addActivity?.success) {
        message.success(data.addActivity.message || 'Activity deleted successfully');
        refetch();
        await fetchActivities();
      } else {
        message.error(data?.addActivity?.message || 'Failed to delete activity');
      }
    } catch (err) {
      console.error('Error deleting activity:', err);
    }
  };

  const handleDeleteTask = async (taskId, taskName) => {
    try {
      const { data } = await addTaskMutation({
        variables: {
          activityId: selectedActivity.id,
          name: taskName || '',
          afterTaskId: 0,
          taskId: taskId,
          status: 0,
        },
      });

      if (data?.addTasks?.success) {
        message.success(data.addTasks.message || 'Task deleted successfully');
        await fetchTasks({
          variables: {
            activityId: selectedActivity.id,
            roleId: roleId
          }
        });
      } else {
        message.error(data?.addTasks?.message || 'Failed to delete task');
      }
    } catch (err) {
      console.error('Error deleting task:', err);
    }
  };

  const handleDeleteSubTask = async (subtaskId, subtaskName) => {
    try {
      const { data } = await addSubtaskMutation({
        variables: {
          taskId: selectedTask.id,
          name: subtaskName || '',
          isCritical: false,
          nonCriticalDescription: '',
          rankForRecommendation: 0,
          afterSubTaskId: -1,
          subtaskId: subtaskId,
          status: 0
        }
      });

      if (data?.addSubtasks?.success) {
        message.success(data.addSubtasks.message || 'Sub-task deleted successfully');
        await refetchSubtasks();
      } else {
        message.error(data?.addSubtasks?.message || 'Failed to delete sub-task');
      }
    } catch (err) {
      console.error('Error deleting sub-task:', err);
    }
  };

  const handleCancelActivity = () => {
    setShowActivityForm(false);
    setActivityTitle('');
    setActivityPosition(null);
    resetActivityForm();
  };

  const handleCancelTask = () => {
    setShowTaskForm(false);
    setTaskTitle('');
    setIsEditingTask(false);
    setEditingTaskId(null);
    setTaskPosition(null);
  };

  const handleCancelSubTask = () => {
    setShowSubTaskForm(false);
    setSubTaskTitle('');
    setIsCriticalSubtask(false);
    setNonCriticalDesc('');
    setSelectedLeadIndicators([]);
    setIsEditingSubTask(false);
    setEditingSubTaskId(null);
    setSubTaskPosition(null);
  };

  const handleActivitySelect = (activity) => {
    setSelectedActivity(activity);
    setSelectedTask(null);
    setSelectedSubTask(null);
    setDescription('');
    setIsCritical(false);
    setDateCreated('');
    fetchTasks({ variables: { activityId: activity.id, roleId: roleId } });
  };

  const handleTaskSelect = (task) => {
    setSelectedTask(task);
    setSelectedSubTask(null);
    setDescription('');
    setIsCritical(false);
    setDateCreated('');
  };

  const handleSubTaskSelect = (subTask) => {
    setSelectedSubTask(subTask);
    setIsCritical(subTask.isCritical);
    setDescription(subTask.nonCriticalDescription || '');
    const formattedDate = subTask.createdAt
      ? dayjs(subTask.createdAt).format('DD/MM/YYYY')
      : '';
    setDateCreated(formattedDate);
  };

  const handleCancelDetails = () => {
    if (selectedSubTask) {
      setDescription(selectedSubTask.nonCriticalDescription || '');
      setIsCritical(selectedSubTask.isCritical);
      setIsEditingDetails(false);
    }
  };

  const resetActivityForm = () => {
    setActivityTitle('');
    setShowActivityForm(false);
    setIsEditingActivity(false);
    setEditingActivityId(null);
    setActivityAfterIdForAdd(0);
  };

  return (
    <>
      
      {/* <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px' }}>
        <Button
          type="text"
          onClick={handleExportCSV}
          disabled={exportLoading}
          style={{ color: 'white', fontWeight: '600', backgroundColor: '#AD0101', borderRadius: '6px' }}
        >
          {exportLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Export CSV'}
        </Button>
      </div> */}
      <div className="activity-management">

        <Row gutter={0} style={{ marginLeft: 0, marginRight: 0 }}>
          {/* Activity Column */}
          <Col span={6} style={{ paddingLeft: 0, paddingRight: 0, borderRight: '1px solid #e8e8e8' }}>
            <div className="column-header">
              <span style={{ fontWeight: '600', fontFamily: "Inter", alignItems: "center" }}>Activity</span>
            </div>

            <div style={{ padding: '16px' }}>
              <Input
                placeholder="Search Activity"
                value={activitySearch}
                onChange={(e) => setActivitySearch(e.target.value)}
                suffix={<SearchOutlined />}
                className="activity-input"
              />

              {!showActivityForm ? (
                <div className="scrollable-list">
                  {activities.length === 0 && (
                    <button
                      type="text"
                      onClick={() => handleAddActivity()}
                      className="know-your-role-add-button"
                    >
                      Add Activity
                    </button>
                  )}

                  {activities.map((activity, index) => (
                    <div
                      key={activity.id}
                      className={`item-box ${selectedActivity?.id === activity.id ? 'active' : ''}`}
                      onClick={() => handleActivitySelect(activity)}
                    >
                      <span>{index + 1}.{activity.name}</span>
                      <div className="item-actions" onClick={(e) => e.stopPropagation()}>
                        <img
                          src={AddingPlus}
                          alt="Add"
                          className="adding-plus-icon"
                          onClick={() => handleAddActivity(activity.id)}
                        />
                        <img
                          src={EditIcon}
                          alt="Edit"
                          className="know-your-edit-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActivityTitle(activity.name);
                            setShowActivityForm(true);
                            setIsEditingActivity(true);
                            setEditingActivityId(activity.id);
                          }}
                        />
                        <img
                          src={DeleteIcon}
                          alt="Delete"
                          className="know-your-delete-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            showDeleteConfirm(
                              'activity',
                              activity.id,
                              activity.name,
                              () => handleDeleteActivity(activity.id)
                            );
                          }}
                        />
                      </div>
                      <RightOutlined />
                    </div>
                  ))}

                  {activities.length > 0 && (
                    <button
                      type="text"
                      onClick={() => handleAddActivity()}
                      className="know-your-role-add-button"
                    >
                      Add Activity
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ marginBottom: '16px' }}>
                  <label className="input-label">
                    Activity <span style={{ color: '#AD0101' }}>*</span>
                  </label>
                  <Input
                    ref={inputRefActivity}
                    placeholder="Enter Activity"
                    value={activityTitle}
                    onChange={(e) => setActivityTitle(e.target.value)}
                    className="activity-input"
                    onFocus={() => console.log('Activity input focused')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveActivity();
                      }
                    }}
                  />

                  {!isEditingActivity && activityAfterIdForAdd !== 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <label className="input-label">Position</label>
                      <Radio.Group
                        value={activityPosition}
                        onChange={(e) => setActivityPosition(e.target.value)}
                      >
                        <Radio value="before">Before Current</Radio>
                        <Radio value="after">After Current</Radio>
                      </Radio.Group>
                    </div>
                  )}

                  <div className="form-buttons">
                    <button style={{ cursor: "pointer" }}
                      className='cancel-button'
                      onClick={handleCancelActivity}>Cancel</button>
                    <button
                      disabled={
                        isSavingActivity ||
                        !activityTitle.trim() ||
                        (!isEditingActivity && activityAfterIdForAdd !== 0 && !activityPosition)
                      }
                      className={`save-button ${!isSavingActivity &&
                        activityTitle.trim() &&
                        (isEditingActivity || activityAfterIdForAdd === 0 || activityPosition)
                        ? 'enabled'
                        : 'disabled'
                        }`}
                      onClick={handleSaveActivity}
                    >
                      {isSavingActivity ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Save'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Col>

          {/* Task Column */}
          <Col span={6} style={{ paddingLeft: 0, paddingRight: 0, borderRight: '1px solid #e8e8e8' }}>
            <div className="column-header">
              <span style={{ fontWeight: '600' }}>Task</span>
            </div>

            <div style={{ padding: '16px' }}>
              <Input
                placeholder="Search Task"
                value={taskSearch}
                onChange={(e) => setTaskSearch(e.target.value)}
                suffix={<SearchOutlined />}
                className="task-input"
              />

              {!showTaskForm ? (
                <div className="scrollable-list">
                  {selectedActivity && (tasksData?.getTaskByActivity || []).length === 0 && (
                    <button
                      type="text"
                      disabled={!selectedActivity}
                      onClick={() => handleAddTask()}
                      className={`know-your-role-add-button ${!selectedActivity ? 'know-your-role-add-button-disabled' : ''}`}
                    >
                      Add Task
                    </button>
                  )}

                  {selectedActivity &&
                    (tasksData?.getTaskByActivity || []).map((task, index) => (
                      <div
                        key={task.id}
                        className={`item-box ${selectedTask?.id === task.id ? 'active' : ''}`}
                        onClick={() => handleTaskSelect(task)}
                      >
                        <span>{index + 1}.{task.name}</span>
                        <div className="item-actions" onClick={(e) => e.stopPropagation()}>
                          <img
                            src={AddingPlus}
                            alt="Add"
                            className="adding-plus-icon"
                            onClick={() => handleAddTask(task.id)}
                          />
                          <img
                            src={EditIcon}
                            alt="Edit"
                            className="know-your-edit-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTaskTitle(task.name);
                              setShowTaskForm(true);
                              setIsEditingTask(true);
                              setEditingTaskId(task.id);
                            }}
                          />
                          <img
                            src={DeleteIcon}
                            alt="Delete"
                            className="know-your-delete-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              showDeleteConfirm(
                                'task',
                                task.id,
                                task.name,
                                () => handleDeleteTask(task.id, task.name)
                              );
                            }}
                          />
                        </div>
                        <RightOutlined />
                      </div>
                    ))}

                  {selectedActivity && (tasksData?.getTaskByActivity || []).length > 0 && (
                    <button
                      type="text"
                      disabled={!selectedActivity}
                      onClick={() => handleAddTask()}
                      className={`know-your-role-add-button ${!selectedActivity ? 'know-your-role-add-button-disabled' : ''}`}
                    >
                      Add Task
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ marginBottom: '16px' }}>
                  <label className="input-label">
                    Task <span style={{ color: '#AD0101' }}>*</span>
                  </label>
                  <Input
                    ref={inputRefTask}
                    placeholder="Enter Task"
                    value={taskTitle}
                    onChange={(e) => setTaskTitle(e.target.value)}
                    className="task-input"
                    onFocus={() => console.log('Task input focused')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveTask();
                      }
                    }}
                  />

                  {!isEditingTask && taskAfterIdForAdd !== 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <label className="input-label">Position</label>
                      <Radio.Group
                        value={taskPosition}
                        onChange={(e) => setTaskPosition(e.target.value)}
                      >
                        <Radio value="before">Before Current</Radio>
                        <Radio value="after">After Current</Radio>
                      </Radio.Group>
                    </div>
                  )}

                  <div className="form-buttons">
                    <button style={{ cursor: "pointer" }}
                      className='cancel-button'
                      onClick={handleCancelTask}>Cancel</button>
                    <button
                      disabled={
                        isSavingTask ||
                        !taskTitle.trim() ||
                        (!isEditingTask && taskAfterIdForAdd !== 0 && !taskPosition)
                      }
                      className={`save-button ${!isSavingTask &&
                        taskTitle.trim() &&
                        (isEditingTask || taskAfterIdForAdd === 0 || taskPosition)
                        ? 'enabled'
                        : 'disabled'
                        }`}
                      onClick={handleSaveTask}
                    >
                      {isSavingTask ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Save'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Col>

          {/* Sub-Task Column */}
          <Col span={6} style={{ paddingLeft: 0, paddingRight: 0, borderRight: '1px solid #e8e8e8' }}>
            <div className="column-header">
              <span style={{ fontWeight: '600' }}>Sub-Task</span>
            </div>

            <div style={{ padding: '16px' }}>
              <Input
                placeholder="Search Sub-task"
                value={subTaskSearch}
                onChange={(e) => setSubTaskSearch(e.target.value)}
                suffix={<SearchOutlined />}
                className="sub-task-input"
              />

              {!showSubTaskForm ? (
                <div className="scrollable-list">
                  {selectedTask && (subtaskData?.listSubtasksForTask || []).length === 0 && (
                    <button
                      type="text"
                      disabled={!selectedTask}
                      onClick={() => handleAddSubTask()}
                      className={`know-your-role-add-button ${!selectedTask ? 'know-your-role-add-button-disabled' : ''}`}
                    >
                      Add Sub-Task
                    </button>
                  )}

                  {selectedTask &&
                    subtaskData?.listSubtasksForTask
                      ?.slice()
                      ?.filter((subTask) => subTask.taskId === selectedTask.id)
                      .map((subTask, index) => (
                        <div
                          key={subTask.id}
                          className={`item-box ${selectedSubTask?.id === subTask.id ? 'active' : ''}`}
                          onClick={() => handleSubTaskSelect(subTask)}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                            {subTask.isCritical && (
                              <img
                                src={DiamondIcon}
                                alt="Critical"
                                style={{ width: '12px', height: '11px', marginTop: '5px' }}
                              />
                            )}
                            <span>{index + 1}.{subTask.name}</span>
                          </div>

                          <div className="item-actions" onClick={(e) => e.stopPropagation()}>
                            <img
                              src={AddingPlus}
                              alt="Add"
                              className="adding-plus-icon"
                              onClick={() => handleAddSubTask(subTask.id)}
                            />
                            <img
                              src={EditIcon}
                              alt="Edit"
                              className="know-your-edit-icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSubTaskTitle(subTask.name);
                                setShowSubTaskForm(true);
                                setIsEditingSubTask(true);
                                setEditingSubTaskId(subTask.id);
                                setIsCriticalSubtask(subTask.isCritical);
                                setNonCriticalDesc(subTask.nonCriticalDescription || '');
                                setSelectedLeadIndicators((subTask.kiis || []).map(k => k.id));
                              }}
                            />
                            <img
                              src={DeleteIcon}
                              alt="Delete"
                              className="know-your-delete-icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                showDeleteConfirm(
                                  'subtask',
                                  subTask.id,
                                  subTask.name,
                                  () => handleDeleteSubTask(subTask.id, subTask.name)
                                );
                              }}
                            />
                          </div>
                          <RightOutlined />
                        </div>
                      ))}

                  {selectedTask && (subtaskData?.listSubtasksForTask || []).length > 0 && (
                    <button
                      type="text"
                      disabled={!selectedTask}
                      onClick={() => handleAddSubTask()}
                      className={`know-your-role-add-button ${!selectedTask ? 'know-your-role-add-button-disabled' : ''}`}
                    >
                      Add Sub-Task
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ marginBottom: '16px' }}>
                  <label className="input-label">
                    Sub-Task <span style={{ color: '#AD0101' }}>*</span>
                  </label>
                  <Input
                    ref={inputRefSubTask}
                    placeholder="Enter Sub-Task"
                    value={subTaskTitle}
                    onChange={(e) => setSubTaskTitle(e.target.value)}
                    className="sub-task-input"
                    onFocus={() => console.log('Subtask input focused')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveSubTask();
                      }
                    }}
                  />

                  {/* <div style={{ marginBottom: '12px' }}>
                  <label className="input-label">
                    Lead Indicator <span style={{ color: '#AD0101' }}>*</span>
                  </label>
                  <Select
                    mode="multiple"
                    placeholder="Select Lead Indicators"
                    value={selectedLeadIndicators}
                    onChange={(values) => setSelectedLeadIndicators(values)}
                    options={leadIndicatorOptions}
                    style={{ width: '100%' }}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </div> */}

                  {!isEditingSubTask && subTaskAfterIdForAdd !== 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <label className="input-label">Position</label>
                      <Radio.Group
                        value={subTaskPosition}
                        onChange={(e) => setSubTaskPosition(e.target.value)}
                      >
                        <Radio value="before">Before Current</Radio>
                        <Radio value="after">After Current</Radio>
                      </Radio.Group>
                    </div>
                  )}

                  <div className="form-buttons">
                    <button style={{ cursor: "pointer" }}
                      className='cancel-button'
                      onClick={handleCancelSubTask}>Cancel</button>
                    <button
                      disabled={
                        isSavingSubTask ||
                        !subTaskTitle.trim() ||
                        // selectedLeadIndicators.length === 0 ||
                        (!isEditingSubTask && subTaskAfterIdForAdd !== 0 && !subTaskPosition)
                      }
                      className={`save-button ${!isSavingSubTask &&
                        subTaskTitle.trim() &&
                        // selectedLeadIndicators.length > 0 &&
                        (isEditingSubTask || subTaskAfterIdForAdd === 0 || subTaskPosition)
                        ? 'enabled'
                        : 'disabled'
                        }`}
                      onClick={handleSaveSubTask}
                    >
                      {isSavingSubTask ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Save'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Col>

          {/* Details Panel */}
          <Col span={6} style={{ paddingLeft: 0, paddingRight: 0 }}>
            <div className="column-header">
              <span style={{ fontWeight: '600' }}>Details</span>
            </div>

            <div style={{ padding: '16px' }} className="scrollable-list">
              {selectedSubTask ? (
                <div>
                  {isEditingDetails ? (
                    <div className="details-group">
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <span className="details-label">Date Created :</span>
                        <span>{dateCreated}</span>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                        <span className="details-label">Critical :</span>
                        <Radio.Group
                          onChange={(e) => setIsCritical(e.target.value)}
                          value={isCritical} // Bind the value to isCritical state
                        >
                          <Radio value={true}>True</Radio>
                          <Radio value={false}>False</Radio>
                        </Radio.Group>
                      </div>

                      <label className="input-label">Description <span style={{ color: '#AD0101' }}>*</span> :</label>
                      <Input.TextArea
                        placeholder="Type Description here..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="details-description"
                        onFocus={() => console.log('Subtask input focused')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            handleSaveDetails();
                          }
                        }}
                      />

                      <div className="details-actions">
                        <button style={{ cursor: "pointer" }}
                          className='cancel-button'
                          onClick={handleCancelDetails}>Cancel</button>
                        <button
                          disabled={isSavingDetails || !description.trim()}
                          className={`save-button ${!isSavingDetails && description.trim() ? 'enabled' : 'disabled'}`}
                          onClick={handleSaveDetails}
                        >
                          {isSavingDetails ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontFamily: "Inter" }}>Date Created : {dateCreated}</div>
                      <div style={{ marginTop: "2%", fontFamily: "Inter" }}>
                        Critical : <strong>{isCritical ? 'True' : 'False'}</strong>
                      </div>

                      <div style={{ marginTop: 8 }}>
                        <span style={{ fontFamily: "Inter" }}>
                          Description <span style={{ color: '#AD0101' }}>*</span> :
                        </span>
                        <p style={{ marginTop: 4, fontFamily: "Inter" }}>{description}</p>
                      </div>

                      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                        <button
                          className='know-your-role-edit-button'
                          style={{ flex: (isCritical && isRootAdmin) ? 'none' : 1 }}
                          onClick={() => setIsEditingDetails(true)}
                        >
                          Edit
                        </button>
                        {isCritical && isRootAdmin && (
                          <button
                            className='know-your-role-tag-button'
                            onClick={() => setShowTaggingModal(true)}
                          >
                            Tag
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div style={{ fontFamily: "Inter" }} className="empty-details">
                  Select a sub-task to view details
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* Delete Confirmation Modal */}
        <Modal
          className="know-your-role-delete-modal"
          title="Confirm Delete"
          open={deleteModal.visible}
          onOk={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          width={560}
          centered
          closeIcon={null}
          footer={[
            <div key="delete-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, width: '100%' }}>
              <Button
                key="cancel"
                style={{ width: 100, height: 40, borderRadius: 6, border: '1px solid #AD0101', backgroundColor: 'white', color: '#AD0101', fontSize: 14, fontWeight: 500, boxShadow: 'none' }}
                onClick={handleDeleteCancel}
              >
                Cancel
              </Button>
              <Button
                key="delete"
                type="primary"
                danger={false}
                style={{ width: 108, height: 40, borderRadius: 6, backgroundColor: '#AD0101', borderColor: '#AD0101', color: 'white', fontSize: 14, fontWeight: 500, boxShadow: 'none' }}
                onClick={handleDeleteConfirm}
                disabled={deleteModal.isDeleting}
              >
                {deleteModal.isDeleting ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Delete'}
              </Button>
            </div>,
          ]}
        >
          <p className="know-your-role-delete-message">
            Are you sure you want to delete the following {deleteModal.type}?
          </p>
          <p className="know-your-role-delete-name">
            "{deleteModal.name}"
          </p>
        </Modal>

        {/* Subtask Tagging Modal */}
        <SubtaskTaggingModal
          visible={showTaggingModal}
          onCancel={() => setShowTaggingModal(false)}
          subtask={selectedSubTask}
          roleId={roleId}
          onTaggingSaved={(data) => {
            message.success(`Tagged ${data.leadIndicatorCount} lead indicators`);
          }}
        />
      </div>
    </>
  );
};

export default KnowYourRole;